import './close-report-screen.scss'

import { defineComponent, PropType } from 'vue'
import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons'

import { ArchiveResource, ConditionReport, SpecimenResource, Wemi } from '@skinsoft/types'
import { ConditionReportRoutes } from '@skinsoft/const'
import { id, referenceNumber } from '@skinsoft/selectors/condition-report-selectors'
import { ConditionReportTransitionStateError } from '@skinsoft/entity'
import { DATE_FORMATS, formatCurrentDate } from '@skinsoft/utils'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { DISPLAY_ALERT_TOPIC, FREE_MEDIA_CACHE_TOPIC } from '@skinsoft/bus/src/busTopics'
import { CLOSE_CLOSE_REPORT_SCREEN_TOPIC } from '../../../busTopics'

export const CloseReportScreen = defineComponent({
    mixins: [reactiveVueMixin],
    props: {
        conditionReport: {
            type: Object as PropType<ConditionReport>,
            required: true,
        },
        associatedEntity: {
            type: Object as PropType<Wemi | ArchiveResource | SpecimenResource>,
            required: true,
        },
        pdfMedias: {
            type: Array as PropType<string[]>,
        },
    },
    template: require('./close-report-screen.html'),
    data() {
        return {
            backIcon: faArrowLeft,
            checkIcon: faCheck,
            closingStatus: "",
            closingInProgress: false,
        }
    },
    methods: {
        closeCloseReportScreen() {
            this.emitOnBus(CLOSE_CLOSE_REPORT_SCREEN_TOPIC)
        },
        async handleCloseReport() {
            this.closingInProgress = true;
            this.closingStatus = this.$t('closeReportScreen.fileGeneration') as string;

            const hasCachedChanges = await this.$conditionReportDataStore.hasCachedUpdates(this.conditionReport.id)
            if(window.navigator.onLine && !hasCachedChanges) {
                this.emitOnBus(FREE_MEDIA_CACHE_TOPIC)
                await new Promise<void>(r => setTimeout(r, 0))
                this.$conditionReportPDFGenerator.generatePDF(
                    this.conditionReport,
                    this.associatedEntity,
                    async (blob) => {
                        this.closingStatus = this.$t('closeReportScreen.transferingFile') as string;

                        const datePrefix = formatCurrentDate(DATE_FORMATS.DD_MM_YYYY_DASH_HMS_ONE_BLOCK)
                        const inventoryNumber = referenceNumber(this.conditionReport)
                        const fileName = datePrefix ?
                            `${inventoryNumber}-${datePrefix}.pdf`
                            : `${datePrefix}.pdf`
                        const withName = new File([blob], fileName)

                        this.$conditionReportPDFTransferHelper.uploadPDFDocument("application/pdf", this.conditionReport.id, withName, true)
                            .subscribe(async (mediaId: string) => {
                                this.closingStatus = this.$t('closeReportScreen.reportClosing') as string;
                                try {
                                    if(mediaId) {
                                        await this.$conditionReportDataStore.closeConditionReport(
                                            id(this.conditionReport),
                                            mediaId
                                        )
                                    }
                                    else {
                                        await this.$conditionReportDataStore.closeConditionReportOffline(id(this.conditionReport))
                                    }

                                    // Going back to closed report screen
                                    this.$router.push({ name: ConditionReportRoutes.CLOSE_REPORTS });
                                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                        message: this.$t('closeReportScreen.notificationContent', { referenceNumber: referenceNumber(this.conditionReport) }),
                                        variant: "success"
                                    })

                                } catch (err) {
                                    if(err instanceof ConditionReportTransitionStateError) {
                                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                            message: this.$t('closeReportScreen.errors.transitionState'),
                                            variant: "danger",
                                        })
                                    } else {
                                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                            message: this.$t('closeReportScreen.errors.pushingPDF'),
                                            variant: "danger",
                                        })
                                    }
                                }
                            })
                    },
                    this.$i18n.locale,
                    this.pdfMedias,
                    false // we don't need to filter pdf as we provide pdf medias ids in excluded ids
                )
                .catch(e => {
                    const alertPayload = {
                        message: this.$t('pdfDocument.generationFailureAlert'),
                        variant: 'danger'
                    };
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                })

            } else {
                await this.$conditionReportDataStore.closeConditionReportOffline(id(this.conditionReport))
                // Going back to closed report screen
                this.$router.push({ name: ConditionReportRoutes.CLOSE_REPORTS });
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('closeReportScreen.notificationContent', { referenceNumber: referenceNumber(this.conditionReport) }),
                    variant: "success"
                })
            }
        },
    },
})
